<div class="support-page">
  <div class="support-hero">
    <div class="support-hero__inner">
      <mat-icon class="support-hero__icon">contact_support</mat-icon>
      <div>
        <h2 class="support-hero__title">Digital Work Order Support</h2>
      </div>
    </div>
  </div>

  <div class="support-body">
    <div class="support-grid">
      <div class="support-card support-card--highlight">
        <div class="support-card__header">
          <mat-icon>headset_mic</mat-icon>
          <h2>Contact Support</h2>
        </div>
        <mat-divider></mat-divider>
        <div class="support-card__body">
          <p class="contact-lead">Before raising a request, please check the FAQs below. If you still need help:</p>
          <ul class="contact-list">
            <li>
              <mat-icon>business</mat-icon>
              <span>
                <strong>AKSO employees:</strong> raise a ticket in
                <a [href]="servicenowUrl" target="_blank" rel="noopener noreferrer">ServiceNow</a>
              </span>
            </li>
            <li>
              <mat-icon>people</mat-icon>
              <span><strong>External users:</strong> contact your project contact person</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- User Guides card -->
      <div class="support-card">
        <div class="support-card__header">
          <mat-icon>menu_book</mat-icon>
          <h2>User Guides</h2>
        </div>
        <mat-divider></mat-divider>
        <div class="support-card__body">
          <p>Step-by-step guides for using Digital Work Order:</p>

          <!-- PDF triggers -->
          <div class="guide-row">
            <mat-icon class="guide-row__icon">picture_as_pdf</mat-icon>
            <span class="guide-row__label">PDF</span>
            <button
              mat-button
              class="guide-trigger-btn"
              [class.guide-trigger-btn--active]="mediaViewer?.type === 'pdf' && mediaViewer?.activeLang === 'en'"
              (click)="openMedia('pdf', 'en')"
            >
              <mat-icon>{{
                mediaViewer?.type === 'pdf' && mediaViewer?.activeLang === 'en' ? 'check_circle' : 'open_in_new'
              }}</mat-icon>
              English
            </button>
            <span class="guide-row__divider">|</span>
            <button
              mat-button
              class="guide-trigger-btn"
              [class.guide-trigger-btn--active]="mediaViewer?.type === 'pdf' && mediaViewer?.activeLang === 'no'"
              (click)="openMedia('pdf', 'no')"
            >
              <mat-icon>{{
                mediaViewer?.type === 'pdf' && mediaViewer?.activeLang === 'no' ? 'check_circle' : 'open_in_new'
              }}</mat-icon>
              Norwegian
            </button>
          </div>

          <!-- Video triggers -->
          <div class="guide-row">
            <mat-icon class="guide-row__icon">videocam</mat-icon>
            <span class="guide-row__label">Video</span>
            <button
              mat-button
              class="guide-trigger-btn"
              [class.guide-trigger-btn--active]="mediaViewer?.type === 'video' && mediaViewer?.activeLang === 'en'"
              (click)="openMedia('video', 'en')"
            >
              <mat-icon>{{
                mediaViewer?.type === 'video' && mediaViewer?.activeLang === 'en'
                  ? 'check_circle'
                  : 'play_circle_outline'
              }}</mat-icon>
              English
            </button>
            <span class="guide-row__divider">|</span>
            <button
              mat-button
              class="guide-trigger-btn"
              [class.guide-trigger-btn--active]="mediaViewer?.type === 'video' && mediaViewer?.activeLang === 'no'"
              (click)="openMedia('video', 'no')"
            >
              <mat-icon>{{
                mediaViewer?.type === 'video' && mediaViewer?.activeLang === 'no'
                  ? 'check_circle'
                  : 'play_circle_outline'
              }}</mat-icon>
              Norwegian
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Full-width media viewer  -->
    @if (mediaViewer && activeMediaItem) {
      <div class="media-viewer">
        <!-- Viewer toolbar -->
        <div class="media-viewer__toolbar">
          <mat-icon class="media-viewer__type-icon">
            {{ mediaViewer.type === 'pdf' ? 'picture_as_pdf' : 'videocam' }}
          </mat-icon>
          <span class="media-viewer__title">{{ mediaViewer.title }}</span>

          <!-- Language tab strip -->
          <div class="media-viewer__lang-tabs">
            @for (item of mediaViewer.items; track item.lang) {
              <button
                class="lang-tab"
                [class.lang-tab--active]="item.lang === mediaViewer.activeLang"
                (click)="switchLang(item.lang)"
              >
                {{ item.label }}
              </button>
            }
          </div>

          <button mat-icon-button class="media-viewer__close" (click)="closeMedia()" aria-label="Close viewer">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- PDF content -->
        @if (mediaViewer.type === 'pdf') {
          <div class="media-viewer__body media-viewer__body--pdf">
            <ngx-extended-pdf-viewer
              appPdfWheelZoom
              [src]="activeMediaItem.url"
              [useBrowserLocale]="true"
              [height]="'75vh'"
              [showOpenFileButton]="false"
              [showDownloadButton]="false"
              [showPrintButton]="true"
              [showSidebarButton]="true"
              [zoom]="'page-width'"
              [zoomLevels]="pdfZoomLevels"
              [textLayer]="true"
            ></ngx-extended-pdf-viewer>
          </div>
        }

        <!-- Video content -->
        @if (mediaViewer.type === 'video') {
          <div class="media-viewer__body media-viewer__body--video">
            <video class="media-viewer__video" controls preload="metadata" [src]="activeMediaItem.url">
              Your browser does not support the video tag.
            </video>
          </div>
        }
      </div>
    }

    <!-- FAQ Section -->
    <div class="support-faq">
      <div class="support-faq__heading">
        <mat-icon>quiz</mat-icon>
        <h2>Frequently Asked Questions</h2>
      </div>
      <mat-accordion class="faq-accordion" multi="false">
        @for (faq of faqs; track faq.question) {
          <mat-expansion-panel class="faq-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ faq.question }}</mat-panel-title>
            </mat-expansion-panel-header>
            <p class="faq-answer">{{ faq.answer }}</p>
            @if (faq.servicenowUrl) {
              <p class="faq-links">
                <a [href]="faq.servicenowUrl" target="_blank" rel="noopener noreferrer">
                  <mat-icon>open_in_new</mat-icon> Raise a ServiceNow request
                </a>
              </p>
            }
          </mat-expansion-panel>
        }
      </mat-accordion>
    </div>
  </div>
</div>
